import {
  paginationWithSelect,
  request,
} from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { DashboardPeriod } from './useDashboardStats';
import type { APIOptions } from '../../types';

export type DashboardOrder = {
  orderId?: string;
  extOrderId?: string;
  enterpriseId?: number;
  clientEmail?: string;
  clientRef?: string;
  orderDate?: string;
  _createdAt?: string;
};

/** APIOptions extended with period for the dashboard listing hooks. */
export type DashboardListOptions = APIOptions & { period?: DashboardPeriod };

type PageParam = { next?: string; pageSize?: number } | undefined;

const PAGE_SIZE = 50;

async function fetchOrdersPage(
  pageParam: PageParam,
  targetOrgId: string,
  period: DashboardPeriod,
) {
  const qs = new URLSearchParams({ period, pageSize: String(PAGE_SIZE) });
  if (pageParam?.next) qs.set('next', pageParam.next);

  return request(`/stats/orders?${qs.toString()}`, {
    method: 'get',
    options: { headers: { 'x-organization': targetOrgId } },
    disableNotif: true,
  });
}

/**
 * Infinite-query hook for the dashboard Orders listing tab.
 *
 * Used both by the full Listing tab (pagination) and by Dashboard.tsx to
 * derive the "Recent activity" feed from the first page.
 *
 * Expects `options.tenantId` = selected `targetOrgId` and
 * `options.period` (via {@link DashboardListOptions}).
 */
export const useDashboardOrders = (options?: DashboardListOptions) => {
  const targetOrgId = String(options?.tenantId ?? '');
  const period: DashboardPeriod = (options?.period as DashboardPeriod) ?? 'week';

  return useInfiniteQuery({
    queryKey: ['dashboard-orders', targetOrgId, period],
    queryFn: ({ pageParam }) =>
      fetchOrdersPage(pageParam as PageParam, targetOrgId, period),
    enabled: Boolean(targetOrgId),
    initialPageParam: undefined,
    retry: 1,
    ...paginationWithSelect<DashboardOrder[]>(),
  });
};
